/**
 * Which queries may be written to the persisted (localStorage) query cache.
 *
 * @location lib/query-cache-persistence.ts
 */

import { defaultShouldDehydrateQuery, type Query } from '@tanstack/react-query';

/**
 * Queries that must NOT be written to localStorage.
 *
 * Measured on production: the persisted cache was 2.24MB on dashboard. and 2.23MB
 * on manage. — and `["builtin-templates"]` alone was 2,128KB of it. Everything
 * else together came to about 48KB.
 *
 * That cost is paid on the critical path, twice over. PersistQueryClientProvider
 * restores SYNCHRONOUSLY before rendering children (deliberately — see the note
 * below), so every page load blocked the main thread on a JSON.parse of 2.2MB;
 * measured at 43.9ms in production, before a single pixel. The persister then
 * re-serialises the whole blob on cache writes. And because localStorage is
 * per-origin, all of it was duplicated on each owner host.
 *
 * These are the wrong things to persist regardless of size: the template
 * catalogue is static, identical for every user, and belongs in the HTTP cache,
 * not in each visitor's localStorage. Excluding it keeps the small
 * session-critical entries (business, branches, profile, billing status) that the
 * synchronous restore exists to serve.
 */
const NEVER_PERSIST_QUERY_KEYS: readonly string[] = [
  'builtin-templates',
  'template-categories',
  'user-templates',
  'template-drafts',
];

/**
 * The persister's dehydrate rule: React Query's default (successful queries
 * only) AND our key filter. Replacing the default persisted PENDING queries
 * with their in-flight `promise`, which JSON turned into `{}`, so the next
 * restore threw "promise.then is not a function".
 */
export function shouldPersistQuery(query: Query): boolean {
  return defaultShouldDehydrateQuery(query) && shouldPersistQueryKey(query.queryKey);
}

export function shouldPersistQueryKey(queryKey: readonly unknown[]): boolean {
  const root = queryKey[0];
  // Root-key match only. A substring test would catch unrelated keys that merely
  // start with the same word.
  return !(typeof root === 'string' && NEVER_PERSIST_QUERY_KEYS.includes(root));
}
